import { useState, useEffect } from 'react';
import { Building2, MapPin, Users, Star, Briefcase, Search, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EntreprisesRecrutentPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/jobs/employers`);
      if (res.ok) {
        const data = await res.json();
        setCompanies(data.employers || []);
      }
    } catch (e) {
      console.error('Error:', e);
      // Données de démo
      setCompanies([
        { id: 1, name: 'BTP Solutions', logo: null, location: 'Lyon', employees: '50-100', rating: 4.5, openJobs: 12, sectors: ['Électricité', 'Plomberie'] },
        { id: 2, name: 'Constructions Modernes', logo: null, location: 'Paris', employees: '200-500', rating: 4.2, openJobs: 25, sectors: ['Gros œuvre', 'Charpente'] },
        { id: 3, name: 'Thermik Pro', logo: null, location: 'Marseille', employees: '20-50', rating: 4.8, openJobs: 5, sectors: ['Chauffage', 'Climatisation'] },
        { id: 4, name: 'Artisan Carrelage', logo: null, location: 'Bordeaux', employees: '10-20', rating: 4.6, openJobs: 3, sectors: ['Carrelage', 'Faïence'] },
        { id: 5, name: 'Bâti France', logo: null, location: 'Toulouse', employees: '100-200', rating: 4.3, openJobs: 18, sectors: ['Maçonnerie', 'Rénovation'] },
        { id: 6, name: 'Électrik Services', logo: null, location: 'Nantes', employees: '30-50', rating: 4.7, openJobs: 8, sectors: ['Électricité', 'Domotique'] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Entreprises qui recrutent</h1>
          <p className="text-gray-600">Découvre les entreprises BTP à la recherche de talents</p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher une entreprise, un secteur..."
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <p className="text-3xl font-bold text-emerald-600">{companies.length}</p>
            <p className="text-sm text-gray-500">Entreprises</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <p className="text-3xl font-bold text-blue-600">{companies.reduce((sum, c) => sum + c.openJobs, 0)}</p>
            <p className="text-sm text-gray-500">Offres actives</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <p className="text-3xl font-bold text-orange-600">15+</p>
            <p className="text-sm text-gray-500">Secteurs</p>
          </div>
        </div>

        {/* Companies list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {companies.map(company => (
              <div key={company.id} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{company.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <MapPin size={14} />
                      <span>{company.location}</span>
                      <span>•</span>
                      <Users size={14} />
                      <span>{company.employees}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <Star size={14} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-medium">{company.rating}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {company.sectors.map((s, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <span className="text-sm">
                    <span className="font-bold text-emerald-600">{company.openJobs}</span>
                    <span className="text-gray-500"> offres ouvertes</span>
                  </span>
                  <Button size="sm" variant="outline">
                    Voir les offres <ExternalLink size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
